import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { FlexCheckInStatuses, FtmReportFilterType, MingaPermission, MingaRoleType } from 'libs/domain';
import { ReportTypes } from 'libs/domain';
import { BehaviorSubject, combineLatest, from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, filter, map, switchMap, take, takeUntil, tap, shareReplay, startWith } from 'rxjs/operators';
import { $enum } from 'ts-enum-util';

import { AnalyticsService } from '@app/src/app/minimal/services/Analytics';
import { PeopleFacadeService } from '@app/src/app/people';
import { PermissionsService } from '@app/src/app/permissions';
import { QueryParamService } from '@app/src/app/shared/services/filtering/query-param.service';
import { withDirtyParams } from '@app/src/app/shared/services/filtering/utils/query-processing.utils';

import { MmScheduledReportsService } from '@modules/minga-manager/components/mm-scheduled-reports/services';

import {
  FilterFormPeopleSearchType,
  FilterFormSelectType,
  FilterFormUserListType,
  FiltersFormData,
  FiltersFormService,
} from '@shared/components/filters-form';
import { FormSelectOption } from '@shared/components/form';
import { SystemAlertSnackBarService } from '@shared/components/system-alert-snackbar';
import { FlexTimePeriodService } from '@shared/services/flex-time';
import { MediaService } from '@shared/services/media';

import { FlexTimeManagerMessages, FlexTimeManagerRoutes } from '../../constants';
import { FtmActivityTemplatesService } from '../ftm-activity-templates/services';
import {
  FlexReportQueryParams,
  flexReportSchemaQuery,
  FtmReportPageOptions,
  FtmReportsMessages,
  SUPPORTS_SCHEDULED_REPORTS,
} from './constants';
import { FtmReportsService } from './services';

@Component({
  selector: 'mg-ftm-reports',
  templateUrl: './ftm-reports.component.html',
  styleUrls: ['./ftm-reports.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [QueryParamService],
})
export class FtmReportsComponent implements OnDestroy, OnInit {
  // Constants

  public readonly FTM_MESSAGES = FlexTimeManagerMessages;
  public readonly MESSAGES = FtmReportsMessages;
  public readonly REPORT_SELECT_OPTIONS = FtmReportPageOptions;
  public readonly REPORT_PAGES = ReportTypes;

  // Table Data Service

  private _dataServiceSubj: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public readonly dataService$: Observable<any> = this._dataServiceSubj.asObservable();

  protected _destroyed = new ReplaySubject<void>(1);
  // Report Type
  public reportType$ = this._router.events.pipe(
    takeUntil(this._destroyed),
    startWith(new NavigationEnd(0, this._router.url, this._router.url)),
    filter(event => event instanceof NavigationEnd),
    map((event: NavigationEnd) => {
      const urlWithoutQueryParams = event.urlAfterRedirects.split('?')[0];
      const urlSegments = urlWithoutQueryParams.split('/');
      const lastSegment = urlSegments?.[urlSegments.length - 1];
      const reportType = $enum(ReportTypes).asValueOrDefault(lastSegment, ReportTypes.FLEX_PERIOD_REGISTERED);
      return reportType;
    }),
  );

  public queryParams$ = this._queryParamService.useQueryParams(flexReportSchemaQuery.parse);

  // Filters

  public readonly activityOptions$: Observable<FormSelectOption<any>[]> = this._ftmActivities.activities$.pipe(
    takeUntil(this._destroyed),
    map(activities =>
      activities.map(activity => ({
        label: activity.name,
        value: activity.id,
      })),
    ),
  );

  public readonly periodOptions$: Observable<FormSelectOption<any>[]> = this.queryParams$.pipe(
    withDirtyParams(),
    filter(({ dirty }) => dirty.start_date || dirty.end_date),
    switchMap(({ params }) =>
      from(this._ftPeriods.fetchAllMinimal(params.start_date, params.end_date)).pipe(
        catchError(() => {
          this._systemAlertSnackBar.error('Failed to fetch all flex time periods');
          return of([]);
        }),
      ),
    ),
    map(periods => this._ftPeriods.formatPeriodsOptions(periods)),
    shareReplay(1),
  );

  public readonly checkinStatusOptions$: Observable<FormSelectOption[]> = of(
    $enum(FlexCheckInStatuses).map(value => ({
      label: value,
      value,
    })),
  );
  public readonly canScheduleReport$ = combineLatest([
    this._permissions.observePermission(MingaPermission.FLEX_TIME_MANAGE),
    this.reportType$,
  ]).pipe(
    map(([canManageFlexTime, reportType]) => {
      return canManageFlexTime && SUPPORTS_SCHEDULED_REPORTS.includes(reportType);
    }),
  );
  // Loading

  private readonly _loadingSubject = new BehaviorSubject<boolean>(true);
  public readonly loading$ = this._loadingSubject.asObservable();

  private readonly _selectedPeople$ = this.queryParams$.pipe(
    withDirtyParams(),
    filter(({ dirty }) => dirty.registrant_hashes || dirty.registerer_hashes),
    switchMap(({ params }) => {
      const hashes = [...(params.registrant_hashes || []), ...(params.registerer_hashes || [])];
      return this._peopleService.getPeopleDetails(hashes).pipe(catchError(() => of([])));
    }),
  );

  public filtersFormStructure$: Observable<FiltersFormData> = combineLatest([
    this.activityOptions$,
    this.periodOptions$,
    this.checkinStatusOptions$,
    this.queryParams$,
    this.reportType$,
    this._selectedPeople$,
  ]).pipe(
    takeUntil(this._destroyed),
    map(([activities, periods, checkinStatuses, params, reportType, selectedPeople]) => {
      const personFilter: Record<string, FilterFormPeopleSearchType> = {
        [FtmReportFilterType.REGISTRANT]: {
          type: 'people-search',
          label: 'Name, ID or email',
          multiple: true,
          value: selectedPeople,
        },
      };

      const activityFilter: Record<string, FilterFormSelectType> = {
        [FtmReportFilterType.ACTIVITIES]: {
          type: 'multi-select',
          label: 'Activity',
          options: activities,
          value: params.activity_ids,
        },
      };

      const periodFilter: Record<string, FilterFormSelectType> = {
        [FtmReportFilterType.PERIODS]: {
          type: reportType === ReportTypes.FLEX_PERIOD_REGISTERED ? 'multi-select' : 'single-select',
          label: 'Period',
          options: periods,
          value: reportType === ReportTypes.FLEX_PERIOD_REGISTERED ? params.period_ids : params.period_ids?.[0],
        },
      };

      const checkInStatusFilter: Record<string, FilterFormSelectType> = {
        [FtmReportFilterType.CHECKIN_STATUSES]: {
          type: 'multi-select',
          label: 'Check in',
          options: checkinStatuses,
          value: params.check_in_statuses,
          selectedItemsToShow: 3,
        },
      };

      const userListFilter: Record<string, FilterFormUserListType> = {
        [FtmReportFilterType.USER_LIST]: {
          type: 'user-list',
          label: 'User list',
          value: params.user_list_ids,
        },
      };

      const makeFlexTimeHistoryReport = (): FiltersFormData => ({
        ...personFilter,
        ...checkInStatusFilter,
        ...periodFilter,
        ...activityFilter,
        ...userListFilter,
      });
      const makeFlexTimeUnregisteredReport = (): FiltersFormData => ({
        ...personFilter,
        ...periodFilter,
        ...userListFilter,
      });
      const makeFlexTimeActivitiesReport = (): FiltersFormData => ({
        ...personFilter,
        ...periodFilter,
        ...activityFilter,
        ...userListFilter,
      });
      const makeStaffReport = (): FiltersFormData => ({
        ...personFilter,
        ...periodFilter,
        ...activityFilter,
        ...userListFilter,
      });
      const makeFlexTimeStudentsReport = (): FiltersFormData => ({
        ...personFilter,
        ...periodFilter,
        ...activityFilter,
        ...userListFilter,
      });

      const getFormData = (): FiltersFormData => {
        if (reportType === ReportTypes.FLEX_PERIOD_REGISTERED) {
          return makeFlexTimeHistoryReport();
        } else if (reportType === ReportTypes.FLEX_PERIOD_UNREGISTERED) {
          return makeFlexTimeUnregisteredReport();
        } else if (reportType === ReportTypes.FLEX_STUDENTS) {
          return makeFlexTimeStudentsReport();
        } else if (reportType === ReportTypes.FLEX_ACTIVITIES) {
          return makeFlexTimeActivitiesReport();
        } else if (reportType === ReportTypes.FLEX_STAFF) {
          return makeStaffReport();
        }
      };

      const formData = getFormData();
      return this._filtersForm.create(formData);
    }),
    tap(() => {
      this._loadingSubject.next(false);
    }),
  );

  // General Variables
  public readonly REPORT_OPTIONS = FtmReportPageOptions;
  // Component Constructor

  constructor(
    public media: MediaService,
    public ftmReports: FtmReportsService,
    private _ftmActivities: FtmActivityTemplatesService,
    private _ftPeriods: FlexTimePeriodService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _analytics: AnalyticsService,
    private _systemAlertSnackBar: SystemAlertSnackBarService,
    private _filtersForm: FiltersFormService,
    private _mmScheduledReportService: MmScheduledReportsService,
    private _permissions: PermissionsService,
    private _queryParamService: QueryParamService<FlexReportQueryParams>,
    private _peopleService: PeopleFacadeService,
  ) {
    this._ftmActivities.fetchAll();
  }

  ngOnInit(): void {
    this.queryParams$.pipe(takeUntil(this._destroyed)).subscribe(params => {
      this._applyFilters(params);
    });
  }

  ngOnDestroy(): void {
    this.ftmReports.clearFilter();
    this._destroyed.next();
    this._destroyed.complete();
  }

  public async onChangeReportType(value: ReportTypes) {
    const snapshot = this._queryParamService.getQueryParamSnapshot(this._route.snapshot, flexReportSchemaQuery.parse);
    await this._queryParamService.updateQueryParams(
      {
        start_date: snapshot.start_date,
        end_date: snapshot.end_date,
      },
      {
        queryParamsHandling: '',
      },
      [FlexTimeManagerRoutes.ROOT, FlexTimeManagerRoutes.REPORTS, value],
    );
  }

  public rangeChanged(range) {
    this._queryParamService.updateQueryParams({
      start_date: range.start,
      end_date: range.end,
      // lets reset any filters for activities/periods since they rely on the date range
      period_ids: [],
      activity_ids: [],
    });
  }

  public async updateFilters(v: any, reportType: ReportTypes) {
    const snapshot = this._queryParamService.getQueryParamSnapshot(this._route.snapshot, flexReportSchemaQuery.parse);

    const isStudent = p => p.roleType === MingaRoleType.STUDENT || p.roleType === MingaRoleType.STUDENT_LEADER;

    const registrants = (v?.[FtmReportFilterType.REGISTRANT] || []).filter(isStudent);
    const registerer = (v?.[FtmReportFilterType.REGISTRANT] || []).filter(p => !isStudent(p));

    const periodIds = v?.[FtmReportFilterType.PERIODS]
      ? Array.isArray(v[FtmReportFilterType.PERIODS])
        ? v[FtmReportFilterType.PERIODS]
        : [v[FtmReportFilterType.PERIODS]]
      : [];

    const params: FlexReportQueryParams = {
      ...snapshot,
      period_ids: periodIds,
      registrant_hashes: registrants.length ? registrants.map(p => p.hash || p.personHash) : null,
      registerer_hashes: registerer.length ? registerer.map(p => p.hash || p.personHash) : null,
      user_list_ids: v?.[FtmReportFilterType.USER_LIST],
      check_in_statuses:
        reportType === ReportTypes.FLEX_PERIOD_REGISTERED ? v?.[FtmReportFilterType.CHECKIN_STATUSES] : null,
      activity_ids: reportType !== ReportTypes.FLEX_PERIOD_UNREGISTERED ? v?.[FtmReportFilterType.ACTIVITIES] : null,
    };

    this._queryParamService.updateQueryParams(params, {
      queryParamsHandling: '',
    });

    // for unregistered report, show a warning if no periods are selected
    if (reportType === ReportTypes.FLEX_PERIOD_UNREGISTERED && periodIds.length === 0) {
      this._systemAlertSnackBar.open({
        type: 'error',
        message: FtmReportsMessages.UNREGISTERED_NO_PERIOD_ERROR,
      });
    }

    this._analytics.sendManagerReport({
      managerType: 'flexTime',
      reportType,
    });
  }

  public onActivate(componentRef: any) {
    this._dataServiceSubj.next(componentRef.ds);
  }

  public async scheduleReport(reportType: ReportTypes) {
    const filters = await this.ftmReports.filter$.pipe(take(1)).toPromise();
    await this._mmScheduledReportService.scheduleReport(reportType, filters);
  }

  public async exportReport(reportType: ReportTypes) {
    this.ftmReports.exportReport(reportType);
  }

  private _applyFilters(params: FlexReportQueryParams) {
    this.ftmReports.setFilter(FtmReportFilterType.START_DATE, params.start_date);
    this.ftmReports.setFilter(FtmReportFilterType.END_DATE, params.end_date);
    this.ftmReports.setFilter(FtmReportFilterType.REGISTRANT, params.registrant_hashes);
    this.ftmReports.setFilter(FtmReportFilterType.REGISTERER, params.registerer_hashes);
    this.ftmReports.setFilter(FtmReportFilterType.ACTIVITIES, params.activity_ids);
    this.ftmReports.setFilter(FtmReportFilterType.PERIODS, params.period_ids);
    this.ftmReports.setFilter(FtmReportFilterType.USER_LIST, params.user_list_ids);
    this.ftmReports.setFilter(FtmReportFilterType.CHECKIN_STATUSES, params.check_in_statuses);

    this.ftmReports.applyFilter();
  }
}
